import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import {
	TabEvent,
} from 'interfaces/app';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import TabulatorImage from 'components/Tabulator/TabulatorImage';
import {
	CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator,
} from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import FlexGroupService from 'services/FlexGroupService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.FlexGroupModel>>();
@Component({
	components: {
		TabulatorBtn,
		TabulatorImage,
	},
	emits: ['routeFlexGroup'],
	mixins: [Template],
})
class FlexGroupTable extends Vue {
	@Ref('addFlexGroup-modal') readonly modal!: BModal;

	@Ref('flexGroupTable')
	private readonly tableReference!: HTMLDivElement;

	private flexgroup : DB.FlexGroupModel[] = [];

	private name?: DB.FlexGroupModel['name'] = '';

	private perPage = 20;

	private isLoaded = false;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private rowData: DB.FlexGroupModel[] = [];

	private table?: Tabulator;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'id',
				title: 'ID',
			},
			{
				title: 'Name',
				field: 'name',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.FlexGroupModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteFlexGroupItem,
		);
		eventBus.on(
			'editItem',
			this.editFlexGroupItem,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteFlexGroupItem,
		);
		eventBus.off(
			'editItem',
			this.editFlexGroupItem,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const flexGroupData = await FlexGroupService.getAll(parameter);
			this.rowData = flexGroupData;
			this.table?.setData(this.rowData);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected addFlexGroup(): void {
		this.isLoaded = true;
		FlexGroupService.create(
			{
				name: this.name,
			},
		).then((res) => {
			this.modal.hide();

			this.flexgroup.push(res);
			this.name = '';
			this.$emit(
				'routeFlexGroup',
				res.id,
			);
			return undefined;
		})
			.finally(() => {
				this.isLoaded = false;
			})
			.catch((err) => {
				this.$toastError(err.message);
			});
	}

	private editFlexGroupItem(data: TabEvent<DB.FlexGroupModel>['editItem']): void {
		this.$emit(
			'routeFlexGroup',
			data.id,
		);
	}

	private deleteFlexGroupItem(data: TabEvent<DB.FlexGroupModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading');
				// eslint-disable-next-line promise/no-nesting
				FlexGroupService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(FlexGroupTable);
